import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const DataRetention: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-28">
            <div className="max-w-4xl mx-auto px-6 md:px-8 py-16">
                {/* Back Link */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[#009B8F] hover:text-[#007d72] transition-colors mb-8 font-medium"
                >
                    <ArrowLeft size={18} />
                    Back to Home
                </Link>

                {/* Header */}
                <div className="mb-12">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#00151a] mb-4">
                        Data Retention Policy
                    </h1>
                    <p className="text-slate-500 text-sm">Last updated: 1st March 2026</p>
                </div>

                {/* Content */}
                <div className="prose prose-slate max-w-none">
                    <div className="space-y-6 text-slate-700 leading-relaxed">
                        <p>
                            This Data Retention Policy explains how Nex Overseas Ltd stores, manages, and deletes personal information collected through our website and services. We only keep personal data for as long as it is needed to provide our services, meet legal requirements, and maintain accurate business records. When information is no longer required, we delete it securely or convert it into anonymised data that cannot be linked back to you.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Account Data</h2>
                        <p>
                            When you create an account, we store your name and email address for as long as your account remains active. We do not store your password; it is encrypted and managed securely by our authentication provider. If you close your account, we remove or anonymise your personal information unless we are legally required to keep certain records.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Consultation Data</h2>
                        <p>
                            During free consultations or assessments, we may collect basic personal details or information you choose to enter on our website. This information is used only to provide your results and improve your experience. If you do not continue with a paid service, this information may be deleted or anonymised after a reasonable period.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Service-Related Data</h2>
                        <p>
                            If you purchase a subscription or request additional services, we may retain information related to education consultancy, coaching and training, accommodation support, travel assistance, or solicitor‑connection services. This may include academic history, preferences, or other details you choose to share. We only collect this information with your permission and keep it only for as long as it is needed to deliver the service. You may request deletion of this information at any time unless we are required by law to retain it.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Payment Records</h2>
                        <p>
                            We do not store payment card details. All payments are processed securely through trusted third‑party providers such as Stripe or PayPal. We only retain basic transaction records required for accounting, fraud prevention, and legal compliance.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Anonymised & Technical Data</h2>
                        <p>
                            We may retain anonymised data to improve our internal systems and platform performance. Anonymised data does not identify you and may be kept indefinitely. Automatically collected information — such as device details, IP addresses, and usage analytics — is stored only for operational, security, and performance purposes and is kept for a limited period.
                        </p>

                        <h2 className="text-2xl font-serif font-bold text-[#00151a] mt-10 mb-4">Your Rights</h2>
                        <p>
                            You have the right to request access to your data, ask for corrections, or request deletion where applicable. To exercise your rights, you can contact us at <a href="mailto:info@nexoverseas.co.uk" className="text-[#009B8F] hover:underline">info@nexoverseas.co.uk</a>. We may update this Data Retention Policy from time to time, and any important changes will be communicated on our website.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataRetention;
